import React, { useMemo } from 'react';

import { RatingSize } from '../../enums/rating';
import Star from './Star';

interface UseReactRatingPropsOptions {
  rate: number;
  size: RatingSize;
  readonly: boolean;
  starColor?: string;
  onChange: (value: number) => void;
}

const STARS_COUNT = 5;

const getRatingSize = (ratingSize: RatingSize) => {
  switch (ratingSize) {
    case RatingSize.Small:
      return [12, 14, null, 18];
    case RatingSize.Large:
      return [16, null, null, 18];
    case RatingSize.ExtraLarge:
      return 27;
    default:
      return [14, null, null, 18];
  }
};

/**
 * We need to slightly modify rate in order to have it displayed properly.
 * That's because right-margin has an impact on the mask generated by react-rating library,
 * resulting in inaccurate mask width (e.g. "3.7" is visible as full star).
 *
 */
export const getInitialRating = (rate: number) => {
  const rateFraction = rate - Math.trunc(rate);

  if (rateFraction <= 0.3) return rate + 0.05;
  if (rateFraction <= 0.6) return rate - 0.05;
  if (rateFraction <= 0.7) return rate - 0.1;

  return rate - 0.2;
};

const renderStars = ({
  rate,
  starSize,
  readonly = true,
  isFull = false,
  fillColor,
  onChange,
}: {
  rate: number;
  starSize: ReturnType<typeof getRatingSize>;
  readonly?: boolean;
  isFull?: boolean;
  fillColor?: string;
  onChange?: (value: number) => void;
}) =>
  Array.from({ length: STARS_COUNT }, (_, index) => {
    const starIndex = index + 1;

    return (
      <Star
        key={starIndex}
        starIndex={starIndex}
        size={starSize}
        readonly={readonly}
        tabIndex={isFull ? (starIndex <= rate ? 0 : -1) : starIndex > rate ? 0 : -1}
        isFull={isFull}
        fillColor={fillColor}
        onChange={onChange}
      />
    );
  });

export const useReactRatingProps = ({
  rate,
  size,
  readonly,
  starColor,
  onChange,
}: UseReactRatingPropsOptions) =>
  useMemo(() => {
    const starSize = getRatingSize(size);

    return {
      readonly,
      onChange,
      initialRating: getInitialRating(rate),
      emptySymbol: renderStars({ rate, starSize, readonly, fillColor: starColor, onChange }),
      fullSymbol: renderStars({
        rate,
        starSize,
        readonly,
        isFull: true,
        fillColor: starColor,
        onChange,
      }),
      height: size === RatingSize.ExtraLarge ? 48 : starSize,
    };
  }, [readonly, onChange, rate, size, starColor]);
